const vehiclePath = /^\/collections\/(?:all\/)?((?:mxpro-ymm-[a-f0-9]+|mxp-fit-[a-z0-9-]+|[a-z0-9-]+-esi\d+))\/?$/;
const safeTag = /^(?:mxpro-ymm-[a-f0-9]+|mxp-fit-[a-z0-9-]+|[a-z0-9-]+-esi\d+)$/;

export function motorQuery(tag, value) {
  if (!safeTag.test(tag)) return null;
  const words = String(value).slice(0, 100).normalize('NFKC').match(/[\p{L}\p{N}]+(?:[-./_][\p{L}\p{N}]+)*/gu) || [];
  const terms = words.map(word => word.toLocaleLowerCase());
  const clauses = terms.map(term => `(title:${term} OR variants.sku:${term} OR variants.barcode:${term})`);
  return clauses.length ? `tag:"${tag}" AND ${clauses.join(' AND ')}` : '';
}

export function readMotorQuery(value) {
  const raw = String(value || '');
  const match = raw.match(/^tag:"([a-z0-9-]+)" AND /u);
  if (!match || !safeTag.test(match[1])) return null;
  const words = [...raw.matchAll(/\(title:([\p{L}\p{N}]+(?:[-./_][\p{L}\p{N}]+)*) OR variants\.sku:/gu)].map(m => m[1]);
  const term = words.join(' ');
  return term && motorQuery(match[1], term) === raw ? {tag: match[1], term} : null;
}

export function motorContext(rows, world, saved, pathname, queryTag, root = '/') {
  if (!['cross', 'road'].includes(world) || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?)?\/?$/i.test(root)) return null;
  const prefix = root.replace(/\/$/, '');
  const entries = [];
  for (const row of rows || []) {
    if (typeof row.make !== 'string' || typeof row.model !== 'string' || !Array.isArray(row.ranges)) continue;
    for (const range of row.ranges) {
      const [start, end, path] = range;
      const tag = typeof path === 'string' && path.match(vehiclePath)?.[1];
      if (!tag || !Number.isInteger(start) || !Number.isInteger(end) || start < 1900 || end > 2030 || start > end) continue;
      entries.push({make: row.make, model: row.model, start, end, tag, path, destination: prefix + path});
    }
  }
  const validSaved = saved?.segment === world && saved.classificationRevision === '20261018review1' && Number.isInteger(saved.year);
  const savedEntries = validSaved ? entries.filter(e => e.make === saved.make && e.model === saved.model && saved.year >= e.start && saved.year <= e.end) : [];
  const routeEntries = queryTag ? entries.filter(e => e.tag === queryTag) : entries.filter(e => e.destination === pathname.replace(/\/$/, ''));
  const candidates = queryTag || routeEntries.length ? routeEntries : savedEntries;
  if (!candidates.length || new Set(candidates.map(e => `${e.make}\0${e.model}\0${e.path}`)).size !== 1) return null;
  const context = candidates[0];
  const selectedYear = savedEntries.some(e => e.tag === context.tag && e.path === context.path) ? saved.year : null;
  return {...context, year: selectedYear || (context.start === context.end ? context.start : `${context.start}–${context.end}`)};
}

if (typeof document !== 'undefined') {
  const form = document.querySelector('[data-mxpro-motor-search]');
  const banner = document.getElementById('mxpro-selected-motor');
  if (form && banner) {
    const world = form.dataset.world;
    const root = form.dataset.root;
    const input = form.querySelector('input[type="search"]');
    const submit = form.querySelector('button[type="submit"]');
    const all = form.querySelector('[data-mxpro-motor-all]');
    const copy = JSON.parse(form.querySelector('[data-motor-search-copy]').textContent);
    let rowsPromise;
    let active = null;
    let updateNumber = 0;

    const savedMotor = () => {
      try { return JSON.parse(localStorage.getItem('mxpro-next-garage-' + world)); } catch { return null; }
    };
    const currentQuery = () => readMotorQuery(new URL(location.href).searchParams.get('q'));
    const retainQuery = () => {
      if (!currentQuery()) return;
      const page = new URL(location.href);
      const raw = page.searchParams.get('q');
      for (const facetForm of document.querySelectorAll('facet-filters-form form')) {
        if (facetForm.id === 'FacetSortForm') continue;
        for (const [name, value] of [['q', raw], ['type', 'product']]) {
          let hidden = facetForm.querySelector(`input[name="${name}"]`);
          if (!hidden) {
            hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = name;
            facetForm.append(hidden);
          }
          if (hidden.value !== value) hidden.value = value;
        }
      }
      for (const link of document.querySelectorAll('.pagination a[href], .facets a[href], .active-facets a[href], nav a[href]')) {
        const url = new URL(link.href, location.origin);
        if (url.origin !== page.origin || url.pathname !== page.pathname || !url.searchParams.has('q')) continue;
        url.searchParams.set('q', raw);
        url.searchParams.set('type', 'product');
        if (world === 'road') url.searchParams.set('view', 'mxpro-road');
        if (page.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', page.searchParams.get('preview_theme_id'));
        if (url.href !== link.href) link.href = url.href;
      }
    };
    const loadRows = () => {
      if (!rowsPromise) rowsPromise = fetch(form.dataset.index, {credentials: 'omit'})
        .then(async response => {
          if (!response.ok) throw Error('Vehicle index unavailable');
          const text = await response.text();
          if (text.length > 1500000) throw Error('Vehicle index too large');
          const data = JSON.parse(text);
          if (data.world !== world || !Array.isArray(data.models) || data.models.length > 3000) throw Error('Invalid vehicle index');
          return data.models;
        }).catch(error => { rowsPromise = null; throw error; });
      return rowsPromise;
    };
    const destination = context => {
      const url = new URL(context.destination, location.origin);
      const page = new URL(location.href);
      for (const [key, value] of page.searchParams) {
        if (key === 'preview_theme_id' || key === 'sort_by' || /^filter\.[a-z0-9_.]+$/i.test(key)) url.searchParams.append(key, value);
      }
      return url;
    };
    const hide = () => {
      if (banner.dataset.motorSearchActive === 'true') banner.hidden = true;
      banner.dataset.motorSearchActive = 'false';
      form.hidden = true;
      input.disabled = true;
      submit.disabled = true;
      active = null;
    };
    const update = async () => {
      const number = ++updateNumber;
      const saved = savedMotor();
      const query = currentQuery();
      if (!saved && !query && !vehiclePath.test(location.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/collections\/)/i, ''))) { hide(); return; }
      try {
        const rows = await loadRows();
        if (number !== updateNumber) return;
        active = motorContext(rows, world, saved, location.pathname, query?.tag, root);
        if (!active) { hide(); return; }
        const name = `${active.make} ${active.model} (${active.year})`;
        banner.querySelector('[data-mxpro-selected-motor-name]').textContent = name;
        banner.dataset.motorSearchActive = 'true';
        banner.hidden = false;
        form.hidden = false;
        input.disabled = false;
        submit.disabled = false;
        all.href = destination(active).href;
        const finder = banner.querySelector('a');
        if (finder) {
          const url = new URL(finder.href, location.origin);
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          finder.href = url.href;
        }
        if (query) {
          retainQuery();
          input.value = query.term;
          const scope = document.querySelector('.mxpro-search-assortment');
          if (scope) scope.textContent = copy.resultsScope.replace('__MOTOR__', name);
          const rawQuery = new URL(location.href).searchParams.get('q');
          const title = document.querySelector('main h1');
          if (title && title.textContent.includes(rawQuery)) title.textContent = title.textContent.replace(rawQuery, query.term);
          for (const globalInput of document.querySelectorAll('input.search__input[name="q"]')) {
            if (globalInput.value === rawQuery) globalInput.value = '';
          }
          if (title) document.title = `${title.textContent} – ${name} – MxProStoreParts`;
          document.querySelectorAll('nav a[href]').forEach(link => {
            if (link.textContent.includes(`tag:"${query.tag}"`)) link.textContent = link.textContent.replace(new URL(location.href).searchParams.get('q'), query.term);
          });
        }
      } catch { if (number === updateNumber) hide(); }
    };
    form.addEventListener('submit', async event => {
      event.preventDefault();
      submit.disabled = true;
      try {
        const rows = await loadRows();
        const context = motorContext(rows, world, savedMotor(), location.pathname, currentQuery()?.tag, root);
        if (!context) { hide(); return; }
        const query = motorQuery(context.tag, input.value);
        if (query === null) { hide(); return; }
        if (!query) { location.assign(destination(context).href); return; }
        const url = new URL(form.action, location.origin);
        url.search = '';
        url.searchParams.set('q', query);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        const page = new URL(location.href);
        for (const [key, value] of page.searchParams) {
          if (key === 'preview_theme_id' || key === 'sort_by' || /^filter\.[a-z0-9_.]+$/i.test(key)) url.searchParams.append(key, value);
        }
        if (world === 'road') url.searchParams.set('view', 'mxpro-road');
        location.assign(url.href);
      } catch { hide(); }
    });
    document.addEventListener('mxpro:vehicle-change', update);
    window.addEventListener('storage', update);
    window.addEventListener('pageshow', update);
    window.addEventListener('popstate', update);
    let retainPending = false;
    new MutationObserver(() => {
      if (retainPending) return;
      retainPending = true;
      requestAnimationFrame(() => { retainPending = false; retainQuery(); });
    }).observe(document.querySelector('main'), {childList: true, subtree: true});
    update();
  }
}
